import {
  EntityGroupEnum,
  IRuleFormResponse,
  PreviewTermsRequest,
  PreviewTermsResponse,
  RuleTypeEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ICustomFieldResSchema } from '../types';
import { apiGetAllList, apiPost, apiPut } from './axios';
import { ApiQueryItem } from './queryUtils';
import { asQueryUtil } from './utils';

const baseRulesKey = ['rules'] as const;

export const rulesV3ServiceQueryKeys = {
  base: [...baseRulesKey, 'rules'] as const,
  uiFormData: (type: RuleTypeEnum, entityGroupType: EntityGroupEnum) =>
    [
      ...rulesV3ServiceQueryKeys.base,
      'ui-form',
      type,
      entityGroupType,
    ] as const,
  previewTerms: (params: {
    quoteId: Maybe<string>;
    quoteModifyDate: Maybe<string>;
    accountId: Maybe<string>;
    wrapListItems: boolean;
    serializedTerms: string;
  }) =>
    [
      ...rulesV3ServiceQueryKeys.base,
      'preview-terms',
      params.quoteId,
      params.quoteModifyDate,
      params.accountId,
      params.wrapListItems,
      params.serializedTerms,
    ] as const,
};

export const approvalV3ServiceQueryKeys = {
  base: [...baseRulesKey, 'approvals'] as const,
  list: () => [...approvalV3ServiceQueryKeys.base, 'list'] as const,
  allList: () => [...approvalV3ServiceQueryKeys.base, 'allList'] as const,
  ruleById: (id: string) => [...approvalV3ServiceQueryKeys.base, id] as const,
};

const approvalsV3QueryItem: Required<Omit<ApiQueryItem, 'delete' | 'upload'>> =
  {
    list: {
      endpoint: `/rules-v3/rules`,
      queryKey: approvalV3ServiceQueryKeys.list(),
      byIdQueryKey: (id: string) => approvalsV3QueryItem.byId.queryKey(id),
    },
    byId: {
      endpoint: (id: string) => `/rules-v3/rules/${id}`,
      queryKey: (id: string) => approvalV3ServiceQueryKeys.ruleById(id),
    },
    create: {
      endpoint: () => `/rules-v3/rules`,
      invalidateKeys: [approvalV3ServiceQueryKeys.list()],
      setDataKey: (id: string) => approvalV3ServiceQueryKeys.ruleById(id),
    },
    update: {
      endpoint: (id: string) => `/rules-v3/rules/${id}`,
      invalidateKeys: [approvalV3ServiceQueryKeys.list()],
      setDataKey: (id: string) => approvalV3ServiceQueryKeys.ruleById(id),
    },
  };

export const validationV3ServiceQueryKeys = {
  base: [...baseRulesKey, 'validations-v3'] as const,
  list: () => [...validationV3ServiceQueryKeys.base, 'list'] as const,
  ruleById: (id: string) => [...validationV3ServiceQueryKeys.base, id] as const,
};

const validationsV3QueryItem: Required<
  Omit<ApiQueryItem, 'delete' | 'upload'>
> = {
  list: {
    endpoint: `/rules-v3/rules`,
    queryKey: validationV3ServiceQueryKeys.list(),
    byIdQueryKey: (id: string) => validationsV3QueryItem.byId.queryKey(id),
  },
  byId: {
    endpoint: (id: string) => `/rules-v3/rules/${id}`,
    queryKey: (id: string) => validationV3ServiceQueryKeys.ruleById(id),
  },
  create: {
    endpoint: () => `/rules-v3/rules`,
    invalidateKeys: [validationV3ServiceQueryKeys.list()],
    setDataKey: (id: string) => validationV3ServiceQueryKeys.ruleById(id),
  },
  update: {
    endpoint: (id: string) => `/rules-v3/rules/${id}`,
    invalidateKeys: [validationV3ServiceQueryKeys.list()],
    setDataKey: (id: string) => validationV3ServiceQueryKeys.ruleById(id),
  },
};

export const conditionalTermV3ServiceQueryKeys = {
  base: [...baseRulesKey, 'conditionalTerms'] as const,
  list: () => [...conditionalTermV3ServiceQueryKeys.base, 'list'] as const,
  ruleById: (id: string) =>
    [...conditionalTermV3ServiceQueryKeys.base, id] as const,
};

const conditionalTermsV3QueryItem: Required<
  Omit<ApiQueryItem, 'delete' | 'upload'>
> = {
  list: {
    endpoint: `/rules-v3/rules`,
    queryKey: conditionalTermV3ServiceQueryKeys.list(),
    byIdQueryKey: (id: string) => conditionalTermsV3QueryItem.byId.queryKey(id),
  },
  byId: {
    endpoint: (id: string) => `/rules-v3/rules/${id}`,
    queryKey: (id: string) => conditionalTermV3ServiceQueryKeys.ruleById(id),
  },
  create: {
    endpoint: () => `/rules-v3/rules`,
    invalidateKeys: [conditionalTermV3ServiceQueryKeys.list()],
    setDataKey: (id: string) => conditionalTermV3ServiceQueryKeys.ruleById(id),
  },
  update: {
    endpoint: (id: string) => `/rules-v3/rules/${id}`,
    invalidateKeys: [conditionalTermV3ServiceQueryKeys.list()],
    setDataKey: (id: string) => conditionalTermV3ServiceQueryKeys.ruleById(id),
  },
};

export const recordAutomationV3ServiceQueryKeys = {
  base: [...baseRulesKey, 'recordAutomation'] as const,
  list: () => [...recordAutomationV3ServiceQueryKeys.base, 'list'] as const,
  ruleById: (id: string) =>
    [...recordAutomationV3ServiceQueryKeys.base, id] as const,

  entityById: (entity: string, id: string) =>
    [...recordAutomationV3ServiceQueryKeys.base, entity, id] as const,
};

const recordAutomationV3QueryItem: Required<
  Omit<ApiQueryItem, 'delete' | 'upload'>
> = {
  list: {
    endpoint: `/rules-v3/rules`,
    queryKey: recordAutomationV3ServiceQueryKeys.list(),
    byIdQueryKey: (id: string) => recordAutomationV3QueryItem.byId.queryKey(id),
  },
  byId: {
    endpoint: (id: string) => `/rules-v3/rules/${id}`,
    queryKey: (id: string) => recordAutomationV3ServiceQueryKeys.ruleById(id),
  },
  create: {
    endpoint: () => `/rules-v3/rules`,
    invalidateKeys: [recordAutomationV3ServiceQueryKeys.list()],
    setDataKey: (id: string) => recordAutomationV3ServiceQueryKeys.ruleById(id),
  },
  update: {
    endpoint: (id: string) => `/rules-v3/rules/${id}`,
    invalidateKeys: [recordAutomationV3ServiceQueryKeys.list()],
    setDataKey: (id: string) => recordAutomationV3ServiceQueryKeys.ruleById(id),
  },
};

export function useArchiveRuleV3(
  options: Partial<
    UseMutationOptions<
      undefined,
      unknown,
      {
        ruleId: string;
        type: RuleTypeEnum;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    undefined,
    unknown,
    {
      ruleId: string;
      type: RuleTypeEnum;
    }
  >({
    mutationFn: ({ ruleId, type }) =>
      apiPut(`/rules-v3/rules/${ruleId}/archive`, {}).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      const { ruleId, type } = variables;
      if (type === RuleTypeEnum.APPROVAL) {
        queryClient.invalidateQueries({
          queryKey: approvalV3ServiceQueryKeys.list(),
        });
        queryClient.invalidateQueries({
          queryKey: approvalV3ServiceQueryKeys.ruleById(ruleId),
        });
        queryClient.invalidateQueries({
          queryKey: approvalV3ServiceQueryKeys.allList(),
        });
      } else if (type === RuleTypeEnum.CONDITIONAL_TERM) {
        queryClient.invalidateQueries({
          queryKey: validationV3ServiceQueryKeys.list(),
        });
        queryClient.invalidateQueries({
          queryKey: conditionalTermV3ServiceQueryKeys.ruleById(ruleId),
        });
      } else if (type === RuleTypeEnum.VALIDATION) {
        queryClient.invalidateQueries({
          queryKey: validationV3ServiceQueryKeys.list(),
        });
        queryClient.invalidateQueries({
          queryKey: validationV3ServiceQueryKeys.ruleById(ruleId),
        });
      }
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export const RULE_SERVICE_V3_API = asQueryUtil({
  approvals: approvalsV3QueryItem,
  validations: validationsV3QueryItem,
  conditionalTerms: conditionalTermsV3QueryItem,
  recordAutomation: recordAutomationV3QueryItem,
});

/**
 * Get all data for Rules V3 UI
 * This first fetches custom fields, then sends that to the rules service
 * to get all the data needed to render the UI
 */
export function useGetRuleFormData({
  entity,
  type,
}: {
  entity: EntityGroupEnum;
  type: RuleTypeEnum;
}) {
  return useQuery({
    queryKey: [...rulesV3ServiceQueryKeys.uiFormData(type, entity)],
    queryFn: () => {
      return apiGetAllList<ICustomFieldResSchema>(
        '/api/configurations/customFields',
      )
        .then((customFields) =>
          apiPost<IRuleFormResponse>(
            '/rules-v3/ui-data',
            { customFields, entity, type },
            {
              // TODO: - remove this once the API is deployed to production
              params: { type: 'QUOTE' },
            },
          ),
        )
        .then((res) => res.data);
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    select: (data) => {
      return {
        ruleFormData: data,
        entities: Object.values(data.fields)?.map(
          ({ entity: currentEntity }) => currentEntity,
        ),
      };
    },
  });
}

/**
 * Resolve {{...}} template expressions in rich-text terms against a
 * caller-supplied quote (and optional account). Used for interactive term
 * previews in the rules test UI and on the quote page.
 */
export function previewQuoteTerms(
  payload: PreviewTermsRequest,
): Promise<PreviewTermsResponse> {
  return apiPost<PreviewTermsResponse>('/rules-v3/preview-terms', payload).then(
    (res) => res.data,
  );
}
